/**
 * More Options Menu Component
 * Context menu for a node's "..." trigger. Rendered via a portal to document.body
 * with position:fixed, explicit screen-pixel coordinates — this keeps it outside any
 * pan/zoom-transformed ancestor, so its position can always be clamped to stay fully
 * inside the visible canvas, even when the node itself sits partly behind the side
 * panels. The menu is still scaled to the live canvas zoom (see `zoom`/`transform`
 * below) so it stays visually proportional to the (already-zoomed) node it's anchored to.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useViewport } from 'reactflow';
import { useUIStore } from '@flow-studio/store';
import { PencilIcon, SettingsIcon, PlugIcon, ClipboardIcon, TrashIcon, DocumentIcon } from '@bizfirst/common-react';

interface MoreOptionsMenuProps {
  nodeId: string;
  onRename: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: () => void;
  onConfigureConnector: () => void;
  onRawConfig?: () => void;
  onClose: () => void;
  /** Outer node element — anchor used to keep the dropdown inside the canvas viewport. */
  anchorEl?: HTMLElement | null;
}

interface FixedPosition {
  top: number;
  left: number;
}

export const MoreOptionsMenu: React.FC<MoreOptionsMenuProps> = ({
  onRename,
  onDelete,
  onDuplicate,
  onEdit,
  onConfigureConnector,
  onRawConfig,
  onClose,
  anchorEl,
}) => {
  const { theme } = useUIStore();
  const { zoom } = useViewport();
  const menuRef = useRef<HTMLDivElement>(null);
  // null until the first layout measurement — menu renders invisible (but measurable) until then.
  const [pos, setPos] = useState<FixedPosition | null>(null);

  // Compute an absolute screen position for the dropdown, clamped to stay fully inside
  // the visible canvas. Recalculated on open, on resize, and whenever the canvas pans/zooms
  // (incl. zoom changes not driven by a wheel gesture, e.g. pinch-zoom, +/- shortcuts, fit-view —
  // those don't fire the wheel listener below that closes the menu on scroll-driven pan/zoom).
  useLayoutEffect(() => {
    const recalc = () => {
      const menuEl = menuRef.current;
      const anchor = anchorEl ?? menuEl?.parentElement ?? null;
      if (!menuEl || !anchor) return;

      const anchorRect = anchor.getBoundingClientRect();
      const menuRect = menuEl.getBoundingClientRect();
      const canvasEl = anchor.closest('.react-flow') as HTMLElement | null;
      const canvasRect = canvasEl
        ? canvasEl.getBoundingClientRect()
        : { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
      const bounds = { top: canvasRect.top, left: canvasRect.left, right: canvasRect.right, bottom: canvasRect.bottom };

      // The node palette / properties side panels and the bottom status footer float over
      // the canvas and can visually obscure content that's technically still inside
      // .react-flow's own box — shrink the usable bounds to whatever's actually
      // unobstructed by them when they're visible.
      const leftPanel = document.querySelector('.left-toolbar');
      if (leftPanel) {
        const r = leftPanel.getBoundingClientRect();
        if (r.bottom > bounds.top && r.top < bounds.bottom) bounds.left = Math.max(bounds.left, r.right);
      }
      const rightPanel = document.querySelector('.right-toolbar');
      if (rightPanel) {
        const r = rightPanel.getBoundingClientRect();
        if (r.bottom > bounds.top && r.top < bounds.bottom) bounds.right = Math.min(bounds.right, r.left);
      }
      const footer = document.querySelector('.flow-footer');
      if (footer) {
        const r = footer.getBoundingClientRect();
        if (r.right > bounds.left && r.left < bounds.right) bounds.bottom = Math.min(bounds.bottom, r.top);
      }

      // Preferred placement matches the original look: hugging the node's top-right corner.
      let top = anchorRect.top;
      let left = anchorRect.right - menuRect.width - 4;

      if (top + menuRect.height > bounds.bottom) top = anchorRect.bottom - menuRect.height;
      if (left < bounds.left) left = anchorRect.left + 4;

      // Final clamp — guarantees full visibility even if the node itself sits partly
      // behind a side panel, where no offset relative to the node's own edge would help.
      const maxLeft = Math.max(bounds.left, bounds.right - menuRect.width);
      const maxTop = Math.max(bounds.top, bounds.bottom - menuRect.height);
      left = Math.min(Math.max(left, bounds.left), maxLeft);
      top = Math.min(Math.max(top, bounds.top), maxTop);

      setPos({ top, left });
    };

    recalc();
    window.addEventListener('resize', recalc);

    // Panning via drag is already caught by the outside-mousedown handler below (it closes
    // the menu on drag-start). Scroll/trackpad panning doesn't fire mousedown, so close on
    // wheel too rather than leaving a stale, now-misaligned menu floating over the canvas.
    const canvasEl = (anchorEl ?? menuRef.current?.parentElement)?.closest('.react-flow');
    canvasEl?.addEventListener('wheel', onClose, { passive: true });

    return () => {
      window.removeEventListener('resize', recalc);
      canvasEl?.removeEventListener('wheel', onClose);
    };
  }, [anchorEl, onClose, zoom]);

  // Close when clicking anywhere outside this menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      // The "..." button that opens this menu already toggles open/closed in its own
      // onClick. This listener runs on mousedown in the capture phase (fires first — see
      // note below), so if it also closed here, the button's click handler would run right
      // after and immediately reopen it, making the button look like it never closes.
      // Scoped to just that button (via a data attribute), not the whole node, so clicking
      // Run/Config/etc. on the same node still closes the menu as expected.
      if ((target as HTMLElement)?.closest?.('[data-more-options-trigger]')) return;
      onClose();
    };
    // Capture phase: React Flow's internal d3-zoom/drag calls stopImmediatePropagation()
    // on pointerdown during pan/drag gesture detection, which stops this listener from
    // ever firing in the bubble phase when the click originates on the canvas pane.
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [onClose]);

  const menuStyle: React.CSSProperties = {
    // Fixed + portaled to document.body — coordinates are plain screen pixels, computed
    // and clamped in the useLayoutEffect above.
    position: 'fixed',
    top: pos ? `${pos.top}px` : '0px',
    left: pos ? `${pos.left}px` : '0px',
    // Hidden until the first measurement resolves a position, so there's no visible flash/jump.
    visibility: pos ? 'visible' : 'hidden',
    // Scale the menu with the canvas so it stays visually proportional to the (already-zoomed)
    // node it's anchored to, instead of staying a fixed pixel size while the node scales.
    transform: `scale(${zoom})`,
    transformOrigin: 'top left',
    background: theme === 'dark' ? '#1e293b' : '#fff',
    border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#e5e7eb'}`,
    borderRadius: '8px',
    boxShadow: '0 8px 28px rgba(0,0,0,0.6)',
    zIndex: 9999,
    minWidth: '185px',
    padding: '6px',
    overflow: 'hidden',
  };

  const menuItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '7px 12px',
    background: 'transparent',
    border: 'none',
    color: theme === 'dark' ? 'rgba(255,255,255,0.75)' : '#374151',
    fontSize: '11px',
    fontWeight: 400,
    cursor: 'pointer',
    textAlign: 'left',
    borderRadius: '6px',
    transition: 'background 0.1s',
  };

  const deleteItemStyle: React.CSSProperties = {
    ...menuItemStyle,
    color: '#ef4444',
  };

  const separatorStyle: React.CSSProperties = {
    margin: '4px 12px',
    borderTop: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
  };

  const hoverBg = theme === 'dark' ? 'rgba(255,255,255,0.07)' : '#f3f4f6';

  const handleItem = (action: () => void) => {
    action();
    onClose();
  };

  return createPortal(
    <div
      ref={menuRef}
      style={menuStyle}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div style={{ padding: '6px 12px 4px', fontSize: '9px', fontWeight: 700, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
        Options
      </div>

      <button
        className="nodrag"
        style={menuItemStyle}
        onClick={() => handleItem(onRename)}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <PencilIcon size={14} />
        <span>Rename</span>
      </button>

      <button
        className="nodrag"
        style={menuItemStyle}
        onClick={() => handleItem(onEdit)}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <SettingsIcon size={14} />
        <span>Edit Properties</span>
      </button>

      <button
        className="nodrag"
        style={menuItemStyle}
        onClick={() => handleItem(onConfigureConnector)}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <PlugIcon size={14} />
        <span>Configure Connector</span>
      </button>

      <button
        className="nodrag"
        style={menuItemStyle}
        onClick={() => handleItem(onDuplicate)}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <ClipboardIcon size={14} />
        <span>Duplicate</span>
      </button>

      {onRawConfig && (
        <button
          className="nodrag"
          style={menuItemStyle}
          onClick={() => handleItem(onRawConfig)}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = hoverBg; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          <DocumentIcon size={14} />
          <span>Raw Config</span>
        </button>
      )}

      <div style={separatorStyle} />

      <button
        className="nodrag"
        style={deleteItemStyle}
        onClick={() => handleItem(onDelete)}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = theme === 'dark' ? 'rgba(239,68,68,0.12)' : '#fee2e2'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <TrashIcon size={14} />
        <span>Delete</span>
      </button>
    </div>,
    document.body
  );
};
